/* ──────────────────────────────────────────────────────────────────────────
   انواع اسناد خزانه — تب «اعتبارات خزانه»

     usePagedList → ۲۰۰ ردیف در هر صفحه (OFFSET/FETCH در بک‌اند)
     splitWrap/topPane/bottomPane → فهرست بالا، گزارش تفکیک طرح پایین

   نکته‌ها:
     * جدید: کد پیشنهادی = MAX(Code)+1 و «منبع اعتبار» و «دستگاه اجرایی»
       از ردیف قبلی پاک نمی‌شوند.
     * «دارای مقدار»: فقط ردیف‌هایی که تخصیص یا تعداد دارند.
     * جستجو روی «عنوان» و «مستند به».
     * انتقال: موارد استفاده به سند جایگزین منتقل می‌شوند.
   ────────────────────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePagedList, CountBadge } from '../../components/paging';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const fmtAmt = n => (Number(n || 0)).toLocaleString('fa-IR');
const fmtCnt = n => (Number(n || 0) ? Number(n).toLocaleString('fa-IR') : '•');

const API = (path, opts = {}) => fetch(`/api/treasury${path}`, {
  headers: { 'Content-Type': 'application/json',
             Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const EMPTY = () => ({
  code: '', title: '', benefit_percent: '',
  issue_date: '', expiry_date: '',
  legal_id: 0, legal: '',
  credit_id: 0, credit: '',
  department_id: 0, department: '',
});

/* ستون‌های گرید اصلی */
const COLS = [
  { key: 'code',            label: 'کد',                             w: 60,  num: true },
  { key: 'title',           label: 'عنوان',                          w: 230 },
  { key: 'benefit_percent', label: 'درصد سود',                       w: 75,  num: true },
  { key: 'issue_date',      label: 'تاریخ انتشار',                   w: 95 },
  { key: 'expiry_date',     label: 'تاریخ سررسید',                   w: 95 },
  { key: 'credit',          label: 'منبع اعتبار',                    w: 190 },
  { key: 'department',      label: 'دستگاه اجرایی',                  w: 190 },
  { key: 'legal',           label: 'مستند به',                       w: 180 },
  { key: 'program_count',   label: 'تعداد طرح',                      w: 80,  num: true },
  { key: 'allocate_before', label: 'تخصیص قبلی',                     w: 120, num: true },
  { key: 'allocate_now',    label: 'تخصیص فعلی',                     w: 120, num: true },
  { key: 'allocate_sum',    label: 'جمع تخصیص',                      w: 125, num: true },
  { key: 'count_before',    label: 'تعداد قرارداد واگذار شده قبلی',  w: 130, num: true },
  { key: 'pay_before',      label: 'مبلغ واگذار شده قبلی',           w: 130, num: true },
  { key: 'count_now',       label: 'تعداد قرارداد واگذار شده فعلی',  w: 130, num: true },
  { key: 'pay_now',         label: 'مبلغ واگذار شده فعلی',           w: 130, num: true },
  { key: 'count_sum',       label: 'تعداد کل قرارداد واگذار شده',    w: 135, num: true },
  { key: 'pay_sum',         label: 'جمع مبالغ واگذار شده',           w: 135, num: true },
  { key: 'remained',        label: 'مانده مصرف نشده',                w: 130, num: true },
];

/* جستجوی انتخابی با فهرست کشویی */
function Lookup({ label, value, endpoint, onPick, flex = 1 }) {
  const [q, setQ]       = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (text) => {
    setQ(text);
    const j = await API(`${endpoint}${endpoint.includes('?') ? '&' : '?'}q=${encodeURIComponent(text)}`);
    if (j.ok) { setRows(j.rows || []); setOpen(true); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, position: 'relative' }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value || q} onFocus={() => search(q)}
        onChange={e => search(e.target.value)} placeholder="جستجو..." style={ui.input} />
      {value && <button onClick={() => { onPick({ id: 0, display: '' }); setQ(''); }}
        style={{ position: 'absolute', left: '6px', top: '30px', border: 'none',
                 background: 'transparent', cursor: 'pointer', color: '#c62828', fontSize: '14px' }}>✕</button>}
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff',
          border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 700, maxHeight: '220px', overflowY: 'auto', marginTop: '2px' }}>
          {rows.map(r => (
            <div key={r.id} onClick={() => { onPick(r); setOpen(false); setQ(''); }}
              style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>{r.display}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TreasuryDocumentList({ user, onBack }) {
  // مجوز دکمه‌های فرم FRMTREASURYDOCUMENT
  const vbf = useFormButtons('FRMTREASURYDOCUMENT', ["BTNNEW","BTNEDIT","BTNREPORT","BTNMOVE","BTNDELETE","BTNOK"]);
  const [year, setYear]           = useState('');
  const [fromDate, setFromDate]   = useState('');
  const [toDate, setToDate]       = useState('');
  const [hasValues, setHasValues] = useState(false);   // فقط ردیف‌های دارای مقدار
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [modal, setModal]         = useState(null);    // 'new' | 'edit'
  const [form, setForm]           = useState(EMPTY());
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState('');
  const [warn, setWarn]           = useState(null);    // WarningDialog
  const [report, setReport]       = useState(null);    // پنل پایین — گزارش تفکیک طرح
  const [move, setMove]           = useState(null);    // پنل انتقال موارد استفاده

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };

  /* فهرست — ۲۰۰ ردیف در هر صفحه */
  const L = usePagedList((page, o) => API(
    `/documents/?page=${page}`
    + `&year=${o.year || ''}&from_date=${o.fromDate || ''}&to_date=${o.toDate || ''}`
    + `&has_values=${o.hasValues ? 1 : 0}&search=${encodeURIComponent(o.search || '')}`));

  const opts = useCallback(() => ({ year, fromDate, toDate, hasValues, search }),
    [year, fromDate, toDate, hasValues, search]);

  /* بار اول — سال مالی و بازهٔ تاریخ را سرور تعیین می‌کند */
  useEffect(() => {
    (async () => {
      const j = await L.reload({ year: '', fromDate: '', toDate: '', hasValues: false, search: '' });
      if (j && j.ok) {
        setYear(String(j.year || ''));
        setFromDate(j.from_date || '');
        setToDate(j.to_date || '');
      }
    })();
    // eslint-disable-next-line
  }, []);

  const reload = () => L.reload(opts());

  /* جستجو — با مکث کوتاه */
  useEffect(() => {
    if (!year) return;
    const t = setTimeout(() => L.reload(opts()), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, hasValues]);

  /* ── جدید / ویرایش ── */
  const openNew = async () => { if (!vbf.can('BTNNEW')) return;
    const j = await API('/documents/next-code/');
    // منبع اعتبار و دستگاه اجرایی عمداً پاک نمی‌شوند
    setForm({
      ...EMPTY(),
      code: j.ok ? String(j.code) : '',
      credit_id:     selected?.credit_id || 0,     credit:     selected?.credit || '',
      department_id: selected?.department_id || 0, department: selected?.department || '',
    });
    setModal('new');
  };

  const openEdit = () => { if (!vbf.can('BTNEDIT')) return;
    if (!selected) return;
    setForm({
      code: String(selected.code ?? ''), title: selected.title || '',
      benefit_percent: String(selected.benefit_percent ?? ''),
      issue_date: selected.issue_date || '', expiry_date: selected.expiry_date || '',
      legal_id: selected.legal_id || 0, legal: selected.legal || '',
      credit_id: selected.credit_id || 0, credit: selected.credit || '',
      department_id: selected.department_id || 0, department: selected.department || '',
    });
    setModal('edit');
  };

  const save = async () => {
    setSaving(true);
    const url  = modal === 'new' ? '/documents/create/' : `/documents/${selected.id}/update/`;
    const j    = await API(url, { method: modal === 'new' ? 'POST' : 'PUT', body: JSON.stringify(form) });
    setSaving(false);
    if (!j.ok) { L.setError(j.msg); return; }
    setModal(null); showToast(j.msg || 'ذخیره شد'); reload();
  };

  /* ── حذف ── */
  const askDelete = () => {
    if (!selected) return;
    setWarn({
      title: 'حذف سند خزانه',
      tone: 'danger',
      lines: [`«${selected.title}» حذف شود؟`],
      confirmLabel: 'حذف شود',
      cancelLabel: 'لغو',
      onConfirm: async () => {
        setWarn(null);
        const j = await API(`/documents/${selected.id}/delete/`, { method: 'DELETE' });
        if (!j.ok) { L.setError(j.msg); return; }
        setSelected(null); showToast('حذف شد'); reload();
      },
    });
  };

  /* ── گزارش — ریز تفکیک طرح در پنل پایین ── */
  const openReport = async () => { if (!vbf.can('BTNREPORT')) return;
    if (!selected) return;
    const j = await API(`/documents/${selected.id}/report/`
      + `?year=${year}&from_date=${fromDate}&to_date=${toDate}`);
    if (!j.ok) { L.setError(j.msg); return; }
    setReport({ title: selected.title, rows: j.data || [] });
  };

  /* ── انتقال موارد استفاده ── */
  const openMove = async () => { if (!vbf.can('BTNMOVE')) return;
    if (!selected) return;
    const j = await API(`/documents/${selected.id}/uses/?year=${year}`);
    if (!j.ok) { L.setError(j.msg); return; }
    if (j.cnt === 0) { setWarn({ title: 'انتقال موارد استفاده', lines: [j.msg],
                                 confirmLabel: 'باشه', cancelLabel: 'بستن',
                                 onConfirm: () => setWarn(null) }); return; }
    setMove({ cnt: j.cnt, cntYear: j.cnt_year, thisYear: true, target: null });
  };

  const doMove = async () => { if (!vbf.can('BTNMOVE')) return;
    const j = await API(`/documents/${selected.id}/move/`, {
      method: 'POST',
      body: JSON.stringify({ target_id: move.target?.id || 0,
                             this_year: move.thisYear ? 1 : 0, year }),
    });
    if (!j.ok) { L.setError(j.msg); return; }
    setMove(null);
    setWarn({
      title: 'انتقال انجام شد',
      lines: ['تغییر انجام شد', 'آیا رکورد فعلی حذف شود؟'],
      confirmLabel: 'رکورد فعلی حذف شود',
      cancelLabel: 'خیر',
      onConfirm: async () => {
        setWarn(null);
        const d = await API(`/documents/${selected.id}/delete/`, { method: 'DELETE' });
        if (!d.ok) L.setError(d.msg); else { setSelected(null); showToast('حذف شد'); }
        reload();
      },
      onCancel: () => { setWarn(null); reload(); },
    });
  };

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* ───────────────────────────── نوار ابزار ───────────────────────────── */
  const toolbar = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <label style={ui.flabel}>سال مالی</label>
        <input value={year} onChange={e => setYear(e.target.value)}
          onBlur={reload} onKeyDown={e => e.key === 'Enter' && reload()} style={ui.yearInput} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '130px' }}>
        <label style={ui.flabel}>از تاریخ</label>
        <JalaliDatePicker value={fromDate} onChange={v => { setFromDate(v); }} placeholder="از تاریخ" />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '130px' }}>
        <label style={ui.flabel}>تا تاریخ</label>
        <JalaliDatePicker value={toDate} onChange={v => { setToDate(v); }} placeholder="تا تاریخ" />
      </div>
      <button onClick={reload} style={{ ...ui.btnEdit, padding: '8px 14px', cursor: 'pointer' }}>
        ⟳ بازخوانی
      </button>

      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px',
                      color: '#444', alignSelf: 'flex-end', cursor: 'pointer' }}>
        <input type="checkbox" checked={hasValues} onChange={e => setHasValues(e.target.checked)} />
        فقط اسناد دارای تخصیص یا واگذاری
      </label>

      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="جستجو در عنوان و مستند به..." style={{ ...ui.searchInput, width: '230px' }} />

      <CountBadge shown={L.data.length} total={L.pager.total} loading={L.loading} unit="سند" />

      <div style={ui.toolBtns}>
        <ExcelButton title="انواع اسناد خزانه"
          subtitle={`سال مالی ${toFa(year)} — از ${toFa(fromDate)} تا ${toFa(toDate)}`}
          columns={COLS} rows={L.data} />
        {vbf.can('BTNNEW') && <button onClick={openNew} style={{ ...ui.btnNew, cursor: 'pointer' }}>+ جدید</button>}
        {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected}
          style={{ ...ui.btnEdit, opacity: selected ? 1 : 0.45, cursor: selected ? 'pointer' : 'default' }}>
          ✎ ویرایش
        </button>}
        {vbf.can('BTNREPORT') && <button onClick={openReport} disabled={!selected}
          style={{ ...ui.btnItems, opacity: selected ? 1 : 0.45, cursor: selected ? 'pointer' : 'default' }}>
          📋 تفکیک طرح
        </button>}
        {vbf.can('BTNMOVE') && <button onClick={openMove} disabled={!selected}
          style={{ ...ui.btnEdit, background: 'linear-gradient(135deg,#4e342e,#6d4c41)',
                   opacity: selected ? 1 : 0.45, cursor: selected ? 'pointer' : 'default' }}>
          ⇄ انتقال موارد استفاده
        </button>}
        {vbf.can('BTNDELETE') && <button onClick={askDelete} disabled={!selected}
          style={{ ...ui.btnDel, opacity: selected ? 1 : 0.45, cursor: selected ? 'pointer' : 'default' }}>
          🗑 حذف
        </button>}
      </div>
    </>
  );

  /* ───────────────────────────── جدول اصلی ───────────────────────────── */
  const grid = (
    <table style={ui.table}>
      <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
        <tr>{COLS.map(c => (
          <th key={c.key} style={{ ...ui.th, minWidth: `${c.w}px` }}>{c.label}</th>
        ))}</tr>
      </thead>
      <tbody>
        {L.data.length === 0 && !L.loading && (
          <tr><td colSpan={COLS.length} style={ui.empty}>رکوردی یافت نشد</td></tr>
        )}
        {L.data.map(r => (
          <tr key={r.id} onClick={() => setSelected(r)}
            style={{ cursor: 'pointer',
                     background: selected?.id === r.id ? '#e8f0fe'
                               : (r.id % 2 ? '#fff' : '#fdf6ec') }}>
            {COLS.map(c => (
              <td key={c.key} style={c.num ? ui.tdNum : ui.td}>
                {c.num
                  ? (['count_before', 'count_now', 'count_sum', 'program_count'].includes(c.key)
                      ? fmtCnt(r[c.key])
                      : (c.key === 'code' || c.key === 'benefit_percent'
                          ? toFa(r[c.key]) : fmtAmt(r[c.key])))
                  : toFa(r[c.key])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  /* ───────────── پنل پایین — گزارش تفکیک طرح ───────────── */
  const reportPane = report && (
    <div style={ui.bottomPane}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '7px 12px', background: '#1a2f50', color: '#fff',
                    position: 'sticky', top: 0, zIndex: 2 }}>
        <span style={{ fontSize: '12.5px', fontWeight: 700 }}>
          تفکیک طرح — {report.title}
          <span style={{ color: '#C8A84B', marginRight: '8px', fontWeight: 400 }}>
            (ارقام به میلیون ریال)
          </span>
        </span>
        <span style={{ display: 'flex', gap: '8px' }}>
          <ExcelButton title={`تفکیک طرح — ${report.title}`} rows={report.rows}
            columns={[{ key: 'program', label: 'طرح' },
                      { key: 'allocate', label: 'تخصیص', num: true },
                      { key: 'paid_before', label: 'پرداختی قبلی', num: true },
                      { key: 'paid_now', label: 'پرداختی فعلی', num: true }]} />
          <button onClick={() => setReport(null)} style={ui.closeBtn}>✕</button>
        </span>
      </div>
      <table style={ui.table}>
        <thead style={{ position: 'sticky', top: '34px', zIndex: 1 }}>
          <tr>
            <th style={{ ...ui.th, minWidth: '320px' }}>طرح</th>
            <th style={{ ...ui.th, minWidth: '120px' }}>تخصیص</th>
            <th style={{ ...ui.th, minWidth: '120px' }}>پرداختی قبلی</th>
            <th style={{ ...ui.th, minWidth: '120px' }}>پرداختی فعلی</th>
          </tr>
        </thead>
        <tbody>
          {report.rows.length === 0 && (
            <tr><td colSpan={4} style={ui.empty}>برای این سند، تخصیصی ثبت نشده است</td></tr>
          )}
          {report.rows.map((r, i) => (
            <tr key={i} style={{ background: i % 2 ? '#fff' : '#fdf6ec' }}>
              <td style={{ ...ui.td, textAlign: 'right' }}>{toFa(r.program)}</td>
              <td style={ui.tdNum}>{fmtAmt(r.allocate)}</td>
              <td style={ui.tdNum}>{fmtAmt(r.paid_before)}</td>
              <td style={ui.tdNum}>{fmtAmt(r.paid_now)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="انواع اسناد خزانه"
        subtitle="اعتبارات خزانه — سامانه ارجان"
        toolbar={toolbar} error={L.error}
        pager={L.pager} onMore={L.more} loading={L.loading} unit="سند">
        {report
          ? (<div style={ui.splitWrap}>
               <div style={ui.topPane}>{grid}</div>
               {reportPane}
             </div>)
          : grid}
      </ListPage>

      {/* ── فرم ثبت/ویرایش ── */}
      {modal && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '720px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <span style={ui.mTitle}>
                {modal === 'new' ? 'سند خزانه جدید' : 'ویرایش سند خزانه'}
              </span>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '110px' }}>
                  <label style={ui.mlabel}>کد</label>
                  <input value={form.code} onChange={e => F('code', e.target.value)} style={ui.input} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 3 }}>
                  <label style={ui.mlabel}>عنوان</label>
                  <input value={form.title} onChange={e => F('title', e.target.value)} style={ui.input} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '110px' }}>
                  <label style={ui.mlabel}>درصد سود</label>
                  <input value={form.benefit_percent}
                    onChange={e => F('benefit_percent', e.target.value)} style={ui.input} />
                </div>
              </div>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>تاریخ انتشار</label>
                  <JalaliDatePicker value={form.issue_date}
                    onChange={v => F('issue_date', v)} placeholder="تاریخ انتشار" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>تاریخ سررسید</label>
                  <JalaliDatePicker value={form.expiry_date}
                    onChange={v => F('expiry_date', v)} placeholder="تاریخ سررسید" />
                </div>
              </div>
              <div style={ui.divider} />
              <div style={ui.row}>
                <Lookup label="مستند به" value={form.legal} endpoint="/documents/lookup/legal/"
                  onPick={r => setForm(f => ({ ...f, legal_id: r.id, legal: r.display }))} />
              </div>
              <div style={ui.row}>
                <Lookup label="منبع اعتبار" value={form.credit} endpoint="/programs/lookup/credit/"
                  onPick={r => setForm(f => ({ ...f, credit_id: r.id, credit: r.display }))} />
              </div>
              <div style={ui.row}>
                <Lookup label="دستگاه اجرایی" value={form.department}
                  endpoint="/programs/lookup/department/"
                  onPick={r => setForm(f => ({ ...f, department_id: r.id, department: r.display }))} />
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal(null)} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>
                {saving ? 'در حال ذخیره…' : 'ذخیره'}
              </button>}
            </div>
          </div>
        </div>
      )}

      {/* ── انتقال موارد استفاده ── */}
      {move && (
        <div style={ui.overlay} onClick={() => setMove(null)}>
          <div style={{ ...ui.modal, maxWidth: '620px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <span style={ui.mTitle}>
                انتقال کلیه موارد استفاده {selected?.title} در جامع مالی ارجان
              </span>
              <button onClick={() => setMove(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              <div style={{ display: 'flex', gap: '18px', marginBottom: '14px', fontSize: '12.5px' }}>
                <span>موارد استفاده در همهٔ سال‌ها: <b>{toFa(move.cnt)}</b></span>
                <span>در سال {toFa(year)}: <b>{toFa(move.cntYear)}</b></span>
              </div>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '14px', fontSize: '12.5px' }}>
                <label style={{ cursor: 'pointer' }}>
                  <input type="radio" checked={move.thisYear}
                    onChange={() => setMove(m => ({ ...m, thisYear: true }))} /> سال {toFa(year)}
                </label>
                <label style={{ cursor: 'pointer' }}>
                  <input type="radio" checked={!move.thisYear}
                    onChange={() => setMove(m => ({ ...m, thisYear: false }))} /> همهٔ سال‌ها
                </label>
              </div>
              <Lookup label="سند خزانهٔ جایگزین" value={move.target?.display || ''}
                endpoint={`/documents/lookup/document/?exclude=${selected?.id || 0}`}
                onPick={r => setMove(m => ({ ...m, target: r.id ? r : null }))} />
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setMove(null)} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNMOVE') && <button onClick={doMove} disabled={!move.target} style={{ ...ui.btnSave,
                opacity: move.target ? 1 : 0.5 }}>انتقال</button>}
            </div>
          </div>
        </div>
      )}

      {warn && (
        <WarningDialog {...warn} onCancel={warn.onCancel || (() => setWarn(null))} />
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}
